<script>
import api from '@/helpers/api'
import axios from 'axios'
import moment from 'moment'
import isEmpty from 'lodash/isEmpty'
import { v4 as uuidv4 } from 'uuid'

const validatSize = (size, max) => {
  if (!max) return true
  // max in MB, converted to Bytes
  const maxBytes = max * 1000000
  return size <= maxBytes
}

export default {
  name: 'file-manager',
  props: {
    directory: { type: String, default: 'uploads' },
    accept: { type: String },
    private: { type: Boolean, default: true },
    maxSize: { type: Number }
  },

  data: vm => ({
    filelist: [],
    __accept: vm.accept
  }),
  computed: {
    startUploadEnabled () {
      return (
        this.filelist.length > 0 &&
        this.filelist.filter(
          f => !f.completed && !f.loading && isEmpty(f.errors)
        ).length > 0
      )
    }
  },
  methods: {
    isEmpty,
    selectFiles (accept) {
      this.$data.__accept = accept
      this.$nextTick(() => {
        this.$refs.file.click()
        this.$data.__accept = this.accept
      })
    },
    onFilesSelected (event) {
      let filteredList = Array.from(this.$refs.file.files)

      this.filelist = [
        ...this.filelist,
        ...filteredList.map(f => {
          let errors = {}
          const { size } = f
          let validSize = validatSize(size, this.maxSize)
          if (!validSize) errors['size'] = `Max. ${this.maxSize} MB!`

          return {
            uid: uuidv4(),
            data: f,
            progress: 0,
            loading: false,
            completed: false,
            errors
          }
        })
      ]
    },
    async onRemoveFileHandler (file) {
      this.filelist = this.filelist.filter(f => f.uid !== file.uid)
      if (file.id) {
        await api.files.delete({ id: file.id })
        this.$emit('file-removed', file)
      }
    },
    remove (i) {
      this.filelist.splice(i, 1)
    },
    dragover (event) {
      event.preventDefault()
      // Add some visual fluff to show the user can drop its files
      if (!event.currentTarget.classList.contains('bg-soft-primary')) {
        event.currentTarget.classList.remove('bg-transparent')
        event.currentTarget.classList.add('bg-soft-primary')
      }
    },
    dragleave (event) {
      // Clean up
      event.currentTarget.classList.add('bg-transparent')
      event.currentTarget.classList.remove('bg-soft-primary')
    },
    drop (event) {
      event.preventDefault()
      this.$refs.file.files = event.dataTransfer.files
      this.onFilesSelected() // Trigger the onFilesSelected event manually
      // Clean up
      event.currentTarget.classList.add('bg-transparent')
      event.currentTarget.classList.remove('bg-soft-primary')
    },
    upload () {
      this.filelist
        .filter(f => !f.completed && !f.loading && isEmpty(f.errors))
        .forEach(file => {
          this.startFileUpload(file)
        })
    },
    async startFileUpload (file) {
      const {
        data: { name, type = 'application/octet-stream', size },
        uid
      } = file
      let key = this.directory || 'uploads'

      file.loading = true

      // get signed upload url
      let { url: uploadUrl, file_id } = await api.files.upload_url({
        file_name: name,
        file_size: size,
        file_content_type: type,
        key
      })
      file.id = file_id
      const acl = this.private ? 'private' : 'public-read'
      var options = {
        headers: {
          'Content-Type': type,
          'x-amz-acl': acl
        },
        onUploadProgress: event => {
          file.progress = Math.round((100 * event.loaded) / event.total)
        }
      }

      let res = await axios.put(uploadUrl, file.data, options)

      file.uploaded_at = moment().format()
      res = await api.files.update({
        id: file_id,
        uploaded_at: file.uploaded_at
      })

      file.loading = false
      file.completed = true

      this.$emit('file-uploaded', { ...file })
      this.filelist = this.filelist.filter(f => f.uid !== file.uid)
    }
  }
}
</script>
<template>
  <div @dragover="dragover" @dragleave="dragleave" @drop="drop">
    <input
      type="file"
      multiple
      name="fields[assetsFieldHandle][]"
      id="assetsFieldHandle"
      class="d-none"
      @change="onFilesSelected"
      ref="file"
      :accept="$data.__accept"
    />
    <div class="d-flex flex-wrap justify-content-start align-items-center">
      <slot name="preset" />
      <template v-for="file in filelist">
        <slot name="file-template" :file="file">
          <div
            :key="file.uid"
            class="d-flex justify-items-start align-items-end p-2 border m-2 rounded-sm"
            style="width:300px;position:relative;"
            :class="{
              'border-danger bg-soft-danger': !isEmpty(file.errors),
              'border-success bg-soft-success': isEmpty(file.errors)
            }"
          >
            <g-file-preview :file="file.data" class="mr-2" />
            <div class="w-100">
              <div
                class="text-truncate font-size-12"
                style="max-width: 160px;"
                v-b-tooltip.hover.v-primary
                :title="file.data.name"
              >
                {{ file.data.name }}
              </div>
              <small
                class="d-block"
                :class="{ 'text-danger': file.errors.size }"
              >
                {{ file.data.size | prettyBytes }}
                {{ file.data.size }}
                <span class="ml-1 text-danger" v-if="file.errors['size']">
                  ({{ file.errors['size'] }})
                </span>
              </small>

              <small class="d-block" v-if="file.uploaded_at">
                Uploaded
                {{ file.uploaded_at | moment('from', 'now') }}
              </small>
              <small class="d-block" v-else>
                Last Modified
                {{ file.data.lastModifiedDate | moment('from', 'now') }}
              </small>
              <b-progress
                v-if="file.loading"
                :value="file.progress"
                :max="100"
                :show-progress="false"
                :animated="!file.completed"
                :rounded="false"
                height="3px"
                variant="success"
                class="w-100 mt-1"
              ></b-progress>
            </div>
            <b-btn
              variant="link"
              size="lg"
              class="m-1 p-1"
              v-on:click="onRemoveFileHandler(file)"
              style="position:absolute;right:0;top:0;"
            >
              <i class="bx bx-x"></i>
            </b-btn>
          </div>
        </slot>
      </template>

      <label for="assetsFieldHandle" class="w-100 pointer p-4 my-5 text-center">
        <div class="d-flex flex-column w-100">
          <i class="display-4 text-primary bx bxs-cloud-upload" />
          <p class="text-muted m-0 p-2">
            Drop your files here or
            <span class="font-weight-bold">click here</span>
          </p>
        </div>
      </label>
    </div>
    <div class="d-flex justify-content-end p-2">
      <b-btn v-on:click="upload" variant="primary" v-if="startUploadEnabled"
        >Start Upload</b-btn
      >
    </div>
  </div>
</template>
